import React from 'react'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { useOportunidadeDetails } from '../hooks/useOportunidadeDetails'
import { OHLCVChart } from '@/modules/graph-view'
import TimeframeSelector from '@/modules/graph-view/components/TimeframeSelector'
import RSIPane from '@/modules/graph-view/components/RSIPane'
import TVChartFixed from '@/modules/graph-view/components/TVChartFixed'
import { useChartType } from '@/modules/graph-view/hooks/useChartType'
import { useCandles } from '@/modules/graph-view/hooks/useCandles'
import { normalizeCandles, filterOutliers } from '@/modules/graph-view/utils/normalize'
import type { TF } from '@/modules/graph-view/types'
import { deriveFiboForOpportunity } from '../utils/fibo'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { getOverlaysForMembers } from '@/modules/settings/services/chartOverlaySettingsService'
import { useUserTimezone } from '@/modules/auth/hooks/useUserTimezone'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import { useFiboTargetsEnabled } from '@/modules/signals/hooks/useFiboTargetsEnabled'
import { useOpportunitySignals } from '@/modules/signals/hooks/useOpportunitySignals'
import { useSignalsOverlayLevels } from '@/modules/signals/hooks/useSignalsOverlayLevels'
import OpportunitySignalsOverlay from './OpportunitySignalsOverlay'
import OpportunityFiboTargetsOverlay from './OpportunityFiboTargetsOverlay'
import type { OportunidadeModalItem } from '../context/OportunidadeModalContext'

function toInterval(data: any, preferredTf?: string | null): TF {
  let tf = String(preferredTf || data?.target_timeframe || data?.timeframe || '').toUpperCase()
  // Normaliza aliases comuns de TF (1D/1W/1M)
  if (tf === '1D') tf = 'D'
  if (tf === '1W') tf = 'W'
  if (tf === '1M') tf = 'M'
  const allowed: TF[] = ['5','15','30','60','240','720','D','W','M']
  return (allowed.includes(tf as TF) ? (tf as TF) : '60')
}

function toSymbol(item: { symbol?: string | null }): string {
  const s = String(item?.symbol || '').toUpperCase().trim()
  return s
}

export default function OportunidadeChartPanel() {
  const { item, open } = useOportunidadeModal()
  const { data } = useOportunidadeDetails(item)
  const symbol = toSymbol(item || {})
  const preferredTf = (item as OportunidadeModalItem | null)?.chartInitialTimeframe ?? null
  const [tf, setTf] = React.useState<TF>(toInterval(data, preferredTf))
  React.useEffect(() => {
    setTf(toInterval(data, preferredTf))
  }, [data?.target_timeframe, data?.timeframe, preferredTf])
  const [refreshTick, setRefreshTick] = React.useState(0)
  React.useEffect(() => { if (open) setRefreshTick((x) => x + 1) }, [open, symbol, tf])
  const { data: candles } = useCandles(symbol, tf, 500, refreshTick)
  const chartRef = React.useRef<any>(null)
  const handleReady = React.useCallback((c: any) => { chartRef.current = c }, [])
  if (!symbol) return <div className="text-sm text-foreground/70">Sem símbolo.</div>
  const bucketed = React.useMemo(()=> normalizeCandles((candles as any) || [], tf), [candles, tf])
  const filtered = React.useMemo(()=> filterOutliers(bucketed as any, tf), [bucketed, tf])
  const [ema12, setEma12] = React.useState(true)
  const [ema26, setEma26] = React.useState(true)
  const [ema200, setEma200] = React.useState(true)
  const [allowed, setAllowed] = React.useState({ ema12: true, ema26: true, ema200: true, fibo: true })
  const { profile } = useAuth()
  const isMaster = profile?.role === 'master'
  const signalsEnabled = useSignalsEnabled()
  const fiboTargetsEnabled = useFiboTargetsEnabled()
  const { signals } = useOpportunitySignals(item?.id ?? null, isMaster || !!signalsEnabled)
  const signalsOverlayLevels = useSignalsOverlayLevels(isMaster || !!signalsEnabled)
  const [showSignalsOverlay, setShowSignalsOverlay] = React.useState(
    !!(item as OportunidadeModalItem | null)?.chartForceSignalsOverlay,
  )
  React.useEffect(() => {
    const typedItem = item as OportunidadeModalItem | null
    if (!typedItem) {
      setShowSignalsOverlay(false)
      return
    }
    if (typedItem.chartForceSignalsOverlay) {
      setShowSignalsOverlay(true)
    } else {
      setShowSignalsOverlay(false)
    }
  }, [item])
  const [showFibo, setShowFibo] = React.useState(false)
  const [showTechV2, setShowTechV2] = React.useState(false)
  React.useEffect(() => {
    let active = true
    getOverlaysForMembers().then((cfg) => {
      if (!active) return
      const next = isMaster ? { ema12: true, ema26: true, ema200: true, fibo: true } : cfg
      setAllowed(next)
      if (!next.ema12) setEma12(false)
      if (!next.ema26) setEma26(false)
      if (!next.ema200) setEma200(false)
      if (!next.fibo) setShowFibo(false)
    }).catch(()=>{})
    return () => { active = false }
  }, [profile?.role])
  const fiboSeriesRef = React.useRef<any[]>([])

  React.useEffect(() => {
    if (!isMaster && !fiboTargetsEnabled) setShowTechV2(false)
  }, [isMaster, fiboTargetsEnabled])

  // TF alvo para Fibo
  const targetTf: TF = React.useMemo(() => {
    let t = String((data as any)?.target_timeframe || (data as any)?.timeframe || tf || '').toUpperCase()
    if (t === '1D') t = 'D'; if (t === '1W') t = 'W'; if (t === '1M') t = 'M'
    const allowed: TF[] = ['5','15','30','60','240','720','D','W','M']
    return (allowed.includes(t as TF) ? (t as TF) : tf)
  }, [data?.target_timeframe, data?.timeframe, tf])

  // Fibo (dados técnicos -> overlay): usa detecção de swing confirmado (SSOT); fallback automático implícito no util
  const [fiboOverlay, setFiboOverlay] = React.useState<{ hi?: number|null; lo?: number|null; f382?: number|null; f50?: number|null; f618?: number|null } | null>(null)
  React.useEffect(() => {
    let cancel = false
    deriveFiboForOpportunity(data as any, { limit: 300, k: 2 })
      .then((res) => { if (!cancel) setFiboOverlay(res || null) })
      .catch(() => { if (!cancel) setFiboOverlay(null) })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, item?.symbol, targetTf])

  // Aplica/remover overlay de Fibo no gráfico
  React.useEffect(() => {
    const chart = chartRef.current
    // cleanup anterior
    try {
      for (const s of fiboSeriesRef.current) {
        try { s._pl && s.removePriceLine && s.removePriceLine(s._pl) } catch {}
        try { chart && s && chart.removeSeries ? chart.removeSeries(s) : s.remove() } catch {}
      }
    } catch {}
    fiboSeriesRef.current = []
    if (!chart || !showFibo || !allowed.fibo || !fiboOverlay) return
    const levels: Array<{ key: string; value: number; color?: string }> = []
    const fmt = (n: number) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 6 })
    const trendRaw = String((data as any)?.target_trend || ((data as any)?.confluence_factors||{}).trend_alvo || '').toLowerCase()
    const isShort = (trendRaw === 'bearish' || trendRaw === 'baixa' || trendRaw === 'short')
    const hiV = Number(fiboOverlay.hi as any)
    const loV = Number(fiboOverlay.lo as any)
    const f382 = Number(fiboOverlay.f382 as any)
    const f50  = Number(fiboOverlay.f50 as any)
    const f618 = Number(fiboOverlay.f618 as any)
    if (!isShort) {
      if (Number.isFinite(hiV)) levels.push({ key: '0', value: hiV, color: 'rgba(148,163,184,0.9)' })
      if (Number.isFinite(f382)) levels.push({ key: '0.382', value: f382, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f50)) levels.push({ key: '0.5', value: f50, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f618)) levels.push({ key: '0.618', value: f618, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(loV)) levels.push({ key: '1', value: loV, color: 'rgba(148,163,184,0.9)' })
    } else {
      if (Number.isFinite(loV)) levels.push({ key: '0', value: loV, color: 'rgba(148,163,184,0.9)' })
      // Para short, 0.382/0.618 invertem (f382_short = f618_long)
      if (Number.isFinite(f618)) levels.push({ key: '0.382', value: f618, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f50)) levels.push({ key: '0.5', value: f50, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(f382)) levels.push({ key: '0.618', value: f382, color: 'rgba(156,163,175,0.9)' })
      if (Number.isFinite(hiV)) levels.push({ key: '1', value: hiV, color: 'rgba(148,163,184,0.9)' })
    }

    if (levels.length === 0) return
    const firstT = Number((filtered as any[])?.[0]?.time)
    const lastT = Number((filtered as any[])?.[(filtered as any[])?.length - 1]?.time)
    if (!Number.isFinite(firstT) || !Number.isFinite(lastT) || firstT >= lastT) return
    for (const lv of levels) {
      try {
        const s = chart.addLineSeries({ color: lv.color || 'rgba(156,163,175,0.9)', lineWidth: 1, lineStyle: 2, lastValueVisible: false, priceLineVisible: false } as any)
        s.setData([{ time: firstT as any, value: lv.value }, { time: lastT as any, value: lv.value }])
        const pl = s.createPriceLine({ price: lv.value, color: lv.color || 'rgba(156,163,175,0.9)', lineStyle: 2, lineWidth: 1, axisLabelVisible: true, title: `${lv.key} (${fmt(lv.value)})` } as any)
        ;(s as any)._pl = pl
        fiboSeriesRef.current.push(s)
      } catch {}
    }
  }, [showFibo, allowed.fibo, fiboOverlay, filtered, tf])
  // Helpers
  function parseIso(s?: any): number | null {
    if (!s) return null
    const t = Date.parse(String(s))
    return Number.isNaN(t) ? null : Math.floor(t/1000)
  }
  function findExtremeTime(candles: any[], fromSec: number, toSec: number, want: 'minLow'|'maxHigh'): number | null {
    if (!Array.isArray(candles) || candles.length===0) return null
    let bestIdx = -1
    for (let i=0;i<candles.length;i++){
      const c = candles[i]
      const t = Number(c?.time)
      if (!Number.isFinite(t)) continue
      if (t < fromSec || t > toSec) continue
      if (bestIdx<0){ bestIdx=i; continue }
      if (want==='minLow'){
        if (Number(c.low) < Number(candles[bestIdx].low)) bestIdx=i
      } else {
        if (Number(c.high) > Number(candles[bestIdx].high)) bestIdx=i
      }
    }
    return bestIdx>=0 ? Number(candles[bestIdx].time) : null
  }

  // Marcadores visuais (bolinha amarela) no melhor ponto (mínima/máxima do movimento de correção)
  const markers = React.useMemo(() => {
    try {
      if (!data) return [] as any[]
      const ot = String(data?.opportunity_type || '').toUpperCase()
      const isLong = ot === 'OVERSOLD_BULLISH'
      const minorTf: TF = String(data?.timeframe || '').toUpperCase() as TF
      const targetTf: TF = String(data?.target_timeframe || '').toUpperCase() as TF
      const openMinorSec = parseIso(data?.open_time)
      if (!openMinorSec) return []
      const cf = (data as any)?.confluence_factors || {}
      const anchorSec = parseIso(cf?.anchor_ts)
        ?? parseIso(cf?.maior_last?.open_time)
        ?? parseIso(cf?.reopened_at)
        ?? parseIso(data?.created_at)
        ?? (openMinorSec - 86400)
      const want: 'minLow'|'maxHigh' = isLong ? 'minLow' : 'maxHigh'
      const pos = (isLong ? 'belowBar' : 'aboveBar') as any
      const color = '#FFD600'
      const base: any = { position: pos, color, shape: 'circle' }
      const out: any[] = []
      if (tf === minorTf || tf === targetTf) {
        const tExtreme = findExtremeTime((bucketed as any[])||[], Number(anchorSec), Number(openMinorSec), want)
        if (tExtreme) out.push({ ...base, time: tExtreme })
      }
      return out
    } catch { return [] }
  }, [data, tf, bucketed])

  const { shouldUseV2 } = useChartType()
  const { timezone } = useUserTimezone()
  const [tvRange, setTvRange] = React.useState<{ from: number; to: number } | null>(null)
  const tvInterval = React.useMemo(() => {
    switch (tf) {
      case '5': return '5'
      case '15': return '15'
      case '30': return '30'
      case '60': return '60'
      case '240': return '240'
      case '720': return '720'
      case 'D': return 'D'
      case 'W': return 'W'
      case 'M': return 'M'
      default: return '60'
    }
  }, [tf])
  return (
    <div className="h-full w-full">
      <div className="mb-2 flex items-center justify-between gap-3">
        <TimeframeSelector value={tf} onChange={setTf} />
        {!shouldUseV2 && (
        <div className="flex items-center gap-4 text-xs">
          {allowed.ema12 && (
            <label className="inline-flex items-center gap-1 select-none">
              <input type="checkbox" className="accent-emerald-400" checked={ema12} onChange={(e)=> setEma12(e.target.checked)} />
              <span className="font-medium" style={{ color: '#FFD600' }}>EMA 12</span>
            </label>
          )}
          {allowed.ema26 && (
            <label className="inline-flex items-center gap-1 select-none">
              <input type="checkbox" className="accent-cyan-400" checked={ema26} onChange={(e)=> setEma26(e.target.checked)} />
              <span className="font-medium" style={{ color: '#00BCD4' }}>EMA 26</span>
            </label>
          )}
          {allowed.ema200 && (
            <label className="inline-flex items-center gap-1 select-none">
              <input type="checkbox" className="accent-violet-500" checked={ema200} onChange={(e)=> setEma200(e.target.checked)} />
              <span className="font-medium" style={{ color: '#8E44AD' }}>EMA 200</span>
            </label>
          )}
          {allowed.fibo && (
            <label className="inline-flex items-center gap-1 select-none">
              <input
                type="checkbox"
                className="accent-slate-400"
                checked={showFibo}
                onChange={(e) => setShowFibo(e.target.checked)}
              />
              <span className="font-medium text-foreground/80">Fibo (alvo)</span>
            </label>
          )}
          {(isMaster || !!fiboTargetsEnabled) && (
            <label className="inline-flex items-center gap-1 select-none">
              <input
                type="checkbox"
                className="accent-emerald-400"
                checked={showTechV2}
                onChange={(e) => setShowTechV2(e.target.checked)}
              />
              <span className="font-medium text-foreground/80">Dados Técnicos V2</span>
            </label>
          )}
          {(isMaster || !!signalsEnabled) && (
            <label className="inline-flex items-center gap-1 select-none">
              <input
                type="checkbox"
                className="accent-amber-400"
                checked={showSignalsOverlay}
                onChange={(e) => setShowSignalsOverlay(e.target.checked)}
              />
              <span className="font-medium" style={{ color: '#FBBF24' }}>Dados Técnicos</span>
            </label>
          )}
        </div>
        )}
      </div>
      <div className="h-[calc(100%-40px)]">
        {shouldUseV2 ? (
          <div className="h-full w-full min-h-[360px] rounded-lg border border-border flex flex-col">
            <div className="flex-1">
              <TVChartFixed
                key={`tv-${symbol}-${tf}`}
                className="h-full w-full"
                symbol={symbol}
                exchange="BYBIT"
                interval={tvInterval}
                studies
                withRsi={false}
                preferPerp
                timezone={timezone}
                onVisibleRangeChange={setTvRange}
              />
            </div>
            <div className="h-[150px] border-t border-border relative overflow-hidden">
              <RSIPane className="h-full w-full" candles={filtered as any} timeframe={tf} externalVisibleRange={tvRange} />
            </div>
          </div>
        ) : (
          <>
            <OHLCVChart
              key={`${symbol}-${tf}`}
              symbol={symbol}
              timeframe={tf}
              timezone={timezone}
              disableVerticalScale
              volume
              onReady={handleReady}
              showInternalControls={false}
              ema12={ema12}
              ema26={ema26}
              ema200={ema200}
              markers={markers as any}
              footerHeight={140}
              footerSlot={(
                <RSIPane className="h-full w-full relative" candles={filtered as any} timeframe={tf} syncTo={chartRef.current} syncLen={filtered.length} />
              )}
            />
            <OpportunityFiboTargetsOverlay
              chartRef={chartRef}
              candles={filtered as any}
              timeframe={tf}
              opportunity={data}
              fibo={fiboOverlay as any}
              enabled={showTechV2 && !!(isMaster || !!fiboTargetsEnabled)}
            />
            <OpportunitySignalsOverlay
              chartRef={chartRef}
              candles={filtered as any}
              timeframe={tf}
              opportunity={data}
              signals={signals}
              overlayLevels={signalsOverlayLevels}
              enabled={!!(isMaster || !!signalsEnabled) && showSignalsOverlay}
            />
          </>
        )}
      </div>
    </div>
  )
}
